import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, FormControlLabel, Checkbox, Autocomplete } from '@mui/material';
import { Add, Delete, Edit, HelpOutline, FileDownload } from '@mui/icons-material';
import api from '../services/api';
import type { Material } from '../types';
import { useDebounce } from '../hooks/useDebounce';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import TextField from '../components/common/TextField';

// Mismos motivos tabulados y mismos roles que el ajuste de stock de Inventario
// (el control real es del servidor; esto solo oculta el botón a quien no puede).
const ADJUST_REASONS = ['Ajuste por corrección', 'Mercadería deteriorada', 'Merma de producción', 'Carga inicial', 'Otro'];
const STOCK_ROLES = ['admin', 'manager', 'propietario', 'gerente', 'logistica', 'calidad'];

type MaterialLot = { id: number; publicId?: string | null; lotCode?: string | null; quantity: number; expirationDate?: string | null; supplierId?: number | null; supplierName?: string | null; purchaseReference?: string | null };

export const MaterialsPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const canAdjustLots = STOCK_ROLES.includes((user?.role || '').toLowerCase());
  const [materials, setMaterials] = useState<Material[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exporting, setExporting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);
  const [form, setForm] = useState<Partial<Material>>({ name: '', unit: '', purchaseUnitName: '', contentPerPurchaseUnit: undefined, defaultWastePct: undefined, cost: 0, currentStock: 0, reorderPoint: 0, primarySupplierId: undefined, isActive: true });
  const [leads, setLeads] = useState<{ supplierId: number; supplierName?: string; leadTimeDays: number }[]>([]);
  const [stockBySupplier, setStockBySupplier] = useState<{ supplierId: number | null; supplierName: string; quantity: number; batchCount: number }[]>([]);
  const [featureFlags, setFeatureFlags] = useState<Set<string>>(new Set());
  const [batchForm, setBatchForm] = useState<{ quantity: number; unitCost: number; purchaseReference: string; supplierId?: number; expirationDate: string }>({
    quantity: 0,
    unitCost: 0,
    purchaseReference: '',
    supplierId: undefined,
    expirationDate: '',
  });

  const supplierMap = useMemo(() => new Map(suppliers.map(s => [s.id, s])), [suppliers]);

  const loadSuppliers = async () => {
    try {
      const res = await api.getSuppliers({ page: 1, pageSize: 1000 });
      setSuppliers(res.suppliers || []);
    } catch {}
  };

  const hasVencimientos = featureFlags.has('vencimientos');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getMaterials({ page, pageSize, search });
      setMaterials(res.materials || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadSuppliers();
    })();
  }, []);

  const debouncedSearch = useDebounce(search, 300);
  const isFirstRender = useRef(true);

  useEffect(() => {
    (async () => {
      await load();
    })();
  }, [page, pageSize]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(1);
    load();
  }, [debouncedSearch]);

  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        setFeatureFlags(new Set((flags || []).filter(f => f.enabled).map(f => f.key.toLowerCase())));
      } catch {
        setFeatureFlags(new Set());
      }
    })();
  }, []);

  const onSearch = async () => {
    setPage(1);
    await load();
  };

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setForm({ name: '', code: '', unit: '', purchaseUnitName: '', contentPerPurchaseUnit: undefined, defaultWastePct: undefined, cost: 0, currentStock: 0, reorderPoint: 0, primarySupplierId: undefined, isActive: true });
    setLeads([]);
    setBatchForm({ quantity: 0, unitCost: 0, purchaseReference: '', supplierId: undefined, expirationDate: '' });
    setOpen(true);
  };

  const openEdit = (m: Material) => {
    setEditing(m);
    setSaveError(null);
    setForm({
      name: m.name,
      code: m.code || '',
      unit: m.unit || '',
      purchaseUnitName: m.purchaseUnitName || '',
      contentPerPurchaseUnit: m.contentPerPurchaseUnit,
      defaultWastePct: m.defaultWastePct,
      cost: m.cost,
      currentStock: m.currentStock,
      reorderPoint: m.reorderPoint,
      primarySupplierId: m.primarySupplierId,
      isActive: m.isActive,
    });
    setBatchForm({ quantity: 0, unitCost: m.cost || 0, purchaseReference: '', supplierId: m.primarySupplierId, expirationDate: '' });
    setOpen(true);
    // load leads
    (async () => {
      try {
        const res = await api.getMaterialSupplierLeads(m.id);
        setLeads(res.items || []);
      } catch { setLeads([]); }
      setLots([]);
      setShowEmptyLots(false);
      await refreshLots(m.id);
    })();
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    if (!form.name) return;
    setSaveError(null);
    try {
      if (editing) {
        await api.updateMaterial(editing.id, form);
      } else {
        await api.createMaterial(form);
      }
      setOpen(false);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Error al guardar la materia prima';
      setSaveError(msg);
      console.error('Error saving material:', e);
    }
  };

  const saveLeads = async () => {
    if (!editing) return; // sólo cuando existe el material
    await api.upsertMaterialSupplierLeads(editing.id, leads.map(l => ({ supplierId: l.supplierId, leadTimeDays: Number(l.leadTimeDays || 0) })));
    // recargar por si cambia algo
    const res = await api.getMaterialSupplierLeads(editing.id);
    setLeads(res.items || []);
  };

  const remove = async (id: number) => {
    if (!window.confirm('¿Eliminar materia prima?')) return;
    await api.deleteMaterial(id);
    await load();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Trae todo el listado (respetando la búsqueda activa) y arma el CSV en el cliente.
      const res = await api.getMaterials({ page: 1, pageSize: 10000, search });
      const rows = res.materials || [];
      const headers = ['Nombre', 'Código', 'Unidad', 'Costo', 'Stock', 'Punto de pedido', 'Proveedor principal'];
      const esc = (v: any) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [headers.join(',')];
      for (const m of rows) {
        lines.push([m.name, m.code || '', m.unit || '', m.cost ?? 0, m.currentStock ?? 0, m.reorderPoint ?? 0, m.primarySupplierName || ''].map(esc).join(','));
      }
      // BOM para que Excel abra el UTF-8 con acentos correctamente.
      const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'materias-primas.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Lotes de la materia prima que se está editando + ajuste manual de la cantidad
  // de cada uno (pedido de Micaela: ver los lotes ahí mismo y corregirlos a mano).
  const [lots, setLots] = useState<MaterialLot[]>([]);
  const [showEmptyLots, setShowEmptyLots] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState<{ lot: MaterialLot; newQuantity: string; supplierId: number | ''; reason: string; notes: string } | null>(null);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const refreshLots = async (materialId: number) => {
    try {
      const res = await api.getMaterialBatches({ materialId });
      setLots(res.items || []);
    } catch { setLots([]); }
    try {
      const sbs = await api.getMaterialStockBySupplier(materialId);
      setStockBySupplier(sbs.items || []);
    } catch { setStockBySupplier([]); }
  };

  const openAdjustLot = (lot: MaterialLot) => {
    setAdjustError(null);
    setAdjustDialog({ lot, newQuantity: String(lot.quantity), supplierId: lot.supplierId ?? '', reason: ADJUST_REASONS[0], notes: '' });
  };

  const saveAdjustLot = async () => {
    if (!adjustDialog || !editing) return;
    const qty = Number(adjustDialog.newQuantity);
    if (adjustDialog.newQuantity === '' || Number.isNaN(qty) || qty < 0) { setAdjustError('Poné una cantidad válida (0 o más)'); return; }
    // Se puede guardar solo el proveedor (sin tocar la cantidad) o solo la cantidad, o ambos.
    const newSupplierId = adjustDialog.supplierId === '' ? null : adjustDialog.supplierId;
    const supplierChanged = newSupplierId !== (adjustDialog.lot.supplierId ?? null);
    if (qty === adjustDialog.lot.quantity && !supplierChanged) { setAdjustError('No cambiaste nada: modificá la cantidad o el proveedor'); return; }
    setAdjustSaving(true);
    setAdjustError(null);
    try {
      const res = await api.adjustMaterialBatch(adjustDialog.lot.id, { newQuantity: qty, reason: adjustDialog.reason, notes: adjustDialog.notes || undefined, updateSupplier: supplierChanged, supplierId: newSupplierId });
      setAdjustDialog(null);
      await refreshLots(editing.id);
      setForm(f => ({ ...f, currentStock: res.currentStock }));
      await load();
    } catch (e: any) {
      setAdjustError(e?.response?.data?.message || 'No se pudo ajustar el lote');
    } finally {
      setAdjustSaving(false);
    }
  };

  const registerBatch = async () => {
    if (!editing) return;
    if (!batchForm.quantity || batchForm.quantity <= 0) return;
    await api.createMaterialBatch(editing.id, {
      quantity: Number(batchForm.quantity),
      unitCost: Number(batchForm.unitCost || 0),
      purchaseReference: batchForm.purchaseReference || undefined,
      supplierId: batchForm.supplierId || undefined,
      expirationDate: batchForm.expirationDate ? new Date(batchForm.expirationDate).toISOString() : undefined,
    });
    setBatchForm({ quantity: 0, unitCost: 0, purchaseReference: '', supplierId: undefined, expirationDate: '' });
    await load();
    await refreshLots(editing.id);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Materias primas</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportCsv} disabled={exporting}>{exporting ? 'Exportando…' : 'Exportar'}</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nueva materia prima</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField size="small" placeholder="Buscar por nombre o código" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
        <Button variant="outlined" onClick={onSearch}>Buscar</Button>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          select
          size="small"
          label="Por página"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 120 }}
        >
          {[20, 50, 100, 200].map((n) => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Unidad</TableCell>
              <TableCell align="right">Costo</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="right">Punto pedido</TableCell>
              <TableCell>Proveedor principal</TableCell>
              <TableCell>Último proveedor</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>{m.name}</TableCell>
                <TableCell>{m.code}</TableCell>
                <TableCell>{m.unit}</TableCell>
                <TableCell align="right">${Number(m.cost || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{m.currentStock}</TableCell>
                <TableCell align="right">{m.reorderPoint}</TableCell>
                <TableCell>{m.primarySupplierName || (m.primarySupplierId ? `#${m.primarySupplierId}` : '-')}</TableCell>
                <TableCell>{m.lastSupplierName || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(m)}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" onClick={() => remove(m.id)}><Delete /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {materials.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary">Sin resultados</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar materia prima' : 'Nueva materia prima'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField required label="Nombre" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
              <TextField label="Código interno" placeholder="SKU, referencia..." value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                freeSolo
                options={[ 'u', 'kg', 'g', 'lt', 'ml', 'm', 'cm', 'mm', 'm2', 'cm2', 'm3' ]}
                value={form.unit || ''}
                onChange={(_, v) => setForm({ ...form, unit: (v as string) || '' })}
                onInputChange={(_, v) => setForm({ ...form, unit: v || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Unidad técnica"
                    placeholder="u, kg, lt, m, m2..."
                    helperText="Se usa en formulaciones y cálculo de rinde"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Tooltip title="Unidad base con la que vas a trabajar en formulaciones y stock. Ej.: kg si tus formulaciones están expresadas en kilos. El costo que cargues es por 1 unidad técnica.">
                            <IconButton size="small" edge="end">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                type="number"
                inputProps={{ step: 'any', min: 0 }}
                label="Costo por unidad"
                value={(form.cost === undefined || form.cost === null) ? '' : form.cost}
                onFocus={(e) => {
                  // Al empezar a tipear, si hay 0, lo limpiamos
                  if ((form.cost as any) === 0) setForm({ ...form, cost: undefined as any });
                  // Selecciona el contenido actual para sobreescribir fácil
                  requestAnimationFrame(() => (e.target as HTMLInputElement).select());
                }}
                onChange={e => {
                  const v = e.target.value;
                  setForm({ ...form, cost: (v === '' ? undefined : Number(v)) as any });
                }}
                placeholder="0.00"
                helperText="Costo unitario del insumo"
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Costo por 1 unidad técnica. Ej.: si ponés unidad técnica kg y la creatina te cuesta 300 USD el kilo puesto en tu depósito, cargá 300 acá.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Unidad de compra"
                placeholder="bolsa, rollo, caja..."
                value={form.purchaseUnitName || ''}
                onChange={e => setForm({ ...form, purchaseUnitName: e.target.value })}
                helperText="Cómo se compra (opcional)"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Formato comercial en el que comprás este insumo. Ej.: 'bolsa', 'bolsa 25 kg', 'tambor 200 kg'. Es descriptivo, no cambia cálculos por sí mismo.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                type="number"
                inputProps={{ step: 'any', min: 0 }}
                label="Contenido por unidad de compra"
                value={form.contentPerPurchaseUnit ?? ''}
                onChange={e => setForm({ ...form, contentPerPurchaseUnit: e.target.value === '' ? undefined : Number(e.target.value) })}
                helperText={`Ej.: 25 si la unidad técnica es ${form.unit || 'kg'} y la bolsa trae 25 kg`}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Cuánta unidad técnica trae cada unidad de compra. Ej.: si la unidad técnica es kg y cada bolsa trae 25 kg de creatina, poné 25. Si la unidad técnica fuera g, pondrías 25000.">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                type="number"
                inputProps={{ step: 'any', min: 0, max: 100 }}
                label="Merma % (defecto)"
                value={form.defaultWastePct ?? ''}
                onChange={e => setForm({ ...form, defaultWastePct: e.target.value === '' ? undefined : Number(e.target.value) })}
                helperText="Porcentaje de merma estimada"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Pérdida estimada entre lo que comprás y lo que realmente podés usar: desperdicio al manipular, producto que se tira, etc. Ej.: si la creatina cuesta 300 USD/kg y ponés una merma del 5 %, el costo real por kg útil será un poco mayor (≈315).">
                      <IconButton size="small" edge="end">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="number"
                inputProps={{ step: '0.001', min: 0 }}
                label="Stock actual"
                value={form.currentStock ?? 0}
                onChange={e => setForm({ ...form, currentStock: Number(e.target.value) })}
                disabled={!!editing}
                helperText={editing ? 'El stock se cambia por Ajuste manual (Inventario → Materias primas), que deja registro de quién y por qué' : 'Unidades disponibles hoy'}
                fullWidth
              />
              <TextField type="number" inputProps={{ step: '1', min: 0 }} label="Punto de pedido (u)" value={form.reorderPoint ?? 0} onChange={e => setForm({ ...form, reorderPoint: Number(e.target.value) })} helperText="Alerta cuando stock ≤ punto de pedido" fullWidth />
            </Stack>
            {hasVencimientos && (
              <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Registrar lote con vencimiento</Typography>
                  {!editing && <Typography variant="caption" color="text.secondary">Guardá primero para habilitar el lote.</Typography>}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    type="number"
                    label="Cantidad (lote)"
                    value={batchForm.quantity || ''}
                    onChange={e => setBatchForm({ ...batchForm, quantity: Number(e.target.value) })}
                    inputProps={{ min: 0, step: 1 }}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    label="Costo unitario"
                    value={batchForm.unitCost || ''}
                    onChange={e => setBatchForm({ ...batchForm, unitCost: Number(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="Fecha de vencimiento"
                    value={batchForm.expirationDate}
                    onChange={e => setBatchForm({ ...batchForm, expirationDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="Referencia de compra"
                    value={batchForm.purchaseReference}
                    onChange={e => setBatchForm({ ...batchForm, purchaseReference: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    select
                    label="Proveedor"
                    value={batchForm.supplierId ?? ''}
                    onChange={e => setBatchForm({ ...batchForm, supplierId: e.target.value ? Number(e.target.value) : undefined })}
                    fullWidth
                  >
                    <MenuItem value="">— Opcional —</MenuItem>
                    {suppliers.map((s: any) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    disabled={!editing || !batchForm.quantity || batchForm.quantity <= 0}
                    onClick={registerBatch}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, minWidth: 180 }}
                  >
                    Registrar lote
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">Al registrar, se suma al stock y se usará en FIFO para consumos de OP.</Typography>
              </Box>
            )}
            <TextField select label="Proveedor principal" value={form.primarySupplierId ?? ''} onChange={e => setForm({ ...form, primarySupplierId: e.target.value ? Number(e.target.value) : undefined })} helperText="Se usa por defecto si no se elige otro proveedor" fullWidth>
              <MenuItem value="">— Ninguno —</MenuItem>
              {suppliers.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>{s.name}{s.company ? ` — ${s.company}` : ''}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Costo total del stock actual</Typography>
                <Typography variant="subtitle1">${Number(((form.cost ?? 0) * (form.currentStock ?? 0)).toFixed(2)).toLocaleString()}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Estado</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel control={<Checkbox checked={!!form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activo" />
                </Stack>
              </Box>
            </Stack>

            {editing && (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Cuánto tarda por proveedor</Typography>
                    <Button size="small" onClick={() => setLeads(prev => [...prev, { supplierId: suppliers[0]?.id, supplierName: suppliers[0]?.name, leadTimeDays: 0 }])}>Agregar</Button>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Proveedor</TableCell>
                        <TableCell align="right">Cuánto tarda (días)</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leads.map((l, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <TextField select size="small" value={l.supplierId} onChange={e => {
                              const v = Number(e.target.value);
                              const row = suppliers.find(s => s.id === v);
                              const next = [...leads];
                              next[idx] = { ...next[idx], supplierId: v, supplierName: row?.name };
                              setLeads(next);
                            }}>
                              {suppliers.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </TextField>
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={l.leadTimeDays} onChange={e => {
                              const next = [...leads]; next[idx] = { ...next[idx], leadTimeDays: Number(e.target.value) };
                              setLeads(next);
                            }} sx={{ width: 120 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" color="error" onClick={() => setLeads(prev => prev.filter((_, i) => i !== idx))}>Quitar</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {leads.length === 0 && (
                        <TableRow><TableCell colSpan={3}><Typography color="text.secondary">Sin proveedores configurados.</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="outlined" size="small" onClick={saveLeads}>Guardar tiempos por proveedor</Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {editing && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Stock por proveedor</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Proveedor</TableCell>
                        <TableCell align="right">Stock</TableCell>
                        <TableCell align="right">Lotes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockBySupplier.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{r.supplierName}</TableCell>
                          <TableCell align="right">{r.quantity}</TableCell>
                          <TableCell align="right">{r.batchCount}</TableCell>
                        </TableRow>
                      ))}
                      {stockBySupplier.length === 0 && (
                        <TableRow><TableCell colSpan={3}><Typography color="text.secondary">Sin stock cargado por lotes todavía.</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {editing && (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Lotes</Typography>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={showEmptyLots} onChange={e => setShowEmptyLots(e.target.checked)} />}
                      label="Ver agotados"
                    />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lote</TableCell>
                          <TableCell>Proveedor</TableCell>
                          <TableCell>Vencimiento</TableCell>
                          <TableCell align="right">Stock</TableCell>
                          {canAdjustLots && <TableCell align="right">Ajustar</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lots.filter(l => showEmptyLots || l.quantity > 0).map(l => (
                          <TableRow key={l.id}>
                            <TableCell>{l.lotCode || l.publicId?.slice(0, 8) || `#${l.id}`}</TableCell>
                            <TableCell>{l.supplierName || 'Sin proveedor'}</TableCell>
                            <TableCell>{l.expirationDate ? new Date(l.expirationDate).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '—'}</TableCell>
                            <TableCell align="right">{l.quantity}</TableCell>
                            {canAdjustLots && (
                              <TableCell align="right">
                                <Tooltip title="Ajustar cantidad de este lote">
                                  <IconButton size="small" onClick={() => openAdjustLot(l)}><Edit fontSize="small" /></IconButton>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {lots.filter(l => showEmptyLots || l.quantity > 0).length === 0 && (
                          <TableRow><TableCell colSpan={canAdjustLots ? 5 : 4}><Typography color="text.secondary">Sin lotes con stock.</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!adjustDialog} onClose={() => setAdjustDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajustar lote {adjustDialog?.lot.lotCode || adjustDialog?.lot.publicId?.slice(0, 8) || ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adjustError && <Alert severity="error">{adjustError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              Cantidad actual del lote: {adjustDialog?.lot.quantity}. Si cambiás la cantidad, el stock total de la materia prima se ajusta por la diferencia y queda registrado en Movimientos.
            </Typography>
            <TextField
              label="Cantidad correcta del lote"
              type="number"
              value={adjustDialog?.newQuantity ?? ''}
              onChange={e => setAdjustDialog(d => (d ? { ...d, newQuantity: e.target.value } : d))}
              fullWidth
            />
            <TextField
              select
              label="Proveedor del lote"
              value={adjustDialog?.supplierId ?? ''}
              onChange={e => setAdjustDialog(d => (d ? { ...d, supplierId: e.target.value === '' ? '' : Number(e.target.value) } : d))}
              fullWidth
            >
              <MenuItem value="">Sin proveedor</MenuItem>
              {suppliers.map((sp: any) => <MenuItem key={sp.id} value={sp.id}>{sp.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Motivo"
              helperText="Solo hace falta si cambia la cantidad"
              value={adjustDialog?.reason || ''}
              onChange={e => setAdjustDialog(d => (d ? { ...d, reason: e.target.value } : d))}
              fullWidth
            >
              {ADJUST_REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              label="Notas (opcional)"
              value={adjustDialog?.notes || ''}
              onChange={e => setAdjustDialog(d => (d ? { ...d, notes: e.target.value } : d))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAdjustLot} disabled={adjustSaving}>{adjustSaving ? 'Guardando…' : 'Guardar ajuste'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialsPage;
